import { useState } from "react";

import {
  AppModal,
  AppText,
  Button,
  ButtonText,
  SearchableSelect,
  Stack,
  TextField,
} from "~/components/ui";
import { CATEGORY_OPTIONS } from "~/features/items/labels";
import { useKits } from "~/state/kits";
import { useItemActions } from "~/state/items";
import type {
  Item,
  ItemAttachment,
  ItemReminder,
} from "~/api/generated/types.gen";
import { planLimitMessage } from "~/utils/plan-limits";

type ItemCategory = Item["category"];

export function AddItemModal({
  visible,
  kitId,
  onClose,
  onCreated,
}: {
  visible: boolean;
  kitId?: string | null;
  onClose: () => void;
  onCreated: (itemId: string) => void;
}) {
  const kits = useKits();
  const actions = useItemActions();
  const [name, setName] = useState("");
  const [brand, setBrand] = useState("");
  const [model, setModel] = useState("");
  const [selectedKitId, setSelectedKitId] = useState(kitId ?? "");
  const [category, setCategory] = useState<ItemCategory>("tool");
  const [error, setError] = useState<string | null>(null);

  const kitOptions = [
    { value: "", label: "Ungrouped" },
    ...kits.map((kit) => ({ value: kit.id, label: kit.name })),
  ];

  async function create() {
    if (!name.trim()) return;
    try {
      const id = await actions.create({
        name,
        brand,
        model,
        category,
        kitId: selectedKitId || kitId || null,
      });
      setName("");
      setBrand("");
      setModel("");
      setError(null);
      onCreated(id);
    } catch (createError) {
      setError(
        planLimitMessage(createError) ??
          (createError instanceof Error
            ? createError.message
            : "Unable to add this item."),
      );
    }
  }

  return (
    <AppModal title="New item" visible={visible} onClose={onClose}>
      <Stack gap="md">
        <Stack gap="xs">
          <AppText size="xs" weight="semibold">
            Name
          </AppText>
          <TextField
            accessibilityLabel="Item name"
            value={name}
            placeholder="18V hammer drill"
            onChangeText={setName}
          />
        </Stack>
        <Stack gap="xs">
          <AppText size="xs" weight="semibold">
            Brand
          </AppText>
          <TextField
            accessibilityLabel="Brand"
            value={brand}
            placeholder="Makita, Apple, Sony..."
            onChangeText={setBrand}
          />
        </Stack>
        <Stack gap="xs">
          <AppText size="xs" weight="semibold">
            Model
          </AppText>
          <TextField
            accessibilityLabel="Model"
            value={model}
            placeholder="DHP484, A7 IV..."
            onChangeText={setModel}
          />
        </Stack>
        <Stack gap="xs">
          <AppText size="xs" weight="semibold">
            Category
          </AppText>
          <SearchableSelect
            title="Category"
            accessibilityLabel="Category"
            value={category}
            options={CATEGORY_OPTIONS}
            onValueChange={(value) => setCategory(value as ItemCategory)}
          />
        </Stack>
        {!kitId ? (
          <Stack gap="xs">
            <AppText size="xs" weight="semibold">
              Kit
            </AppText>
            <SearchableSelect
              title="Kit"
              accessibilityLabel="Kit"
              value={selectedKitId}
              options={kitOptions}
              placeholder="Ungrouped"
              onValueChange={setSelectedKitId}
            />
          </Stack>
        ) : null}
        {error ? (
          <AppText size="xs" tone="danger">
            {error}
          </AppText>
        ) : null}
        <Button
          iconName="plus"
          disabled={!name.trim()}
          onPress={() => void create()}
        >
          <ButtonText>Add item</ButtonText>
        </Button>
      </Stack>
    </AppModal>
  );
}
